import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { requestPasswordReset } from '../services/adminAccess';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false), [notice, setNotice] = useState('');
  
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError('');
      setLoading(true);
      setNotice('');
      if (resetMode) {
        const result = await requestPasswordReset(email);
        if (result.error) throw result.error;
        setNotice('If an account exists for this email, you will receive a password reset link.');
        return;
      }
      
      const { data, error } = await signIn(email, password);
      
      if (error) {
        setError(error.message);
        console.error('Login error:', error);
      } else if (data?.user) {
        // Wait a moment for the auth state to update
        setTimeout(() => {
          const from = location.state?.from;
          navigate(typeof from === 'string' && (from.startsWith('/admin/') || ['/dev/door-leaf', '/dev/render-batch', '/dev/drawing-editor'].includes(from.split('?')[0])) ? from : '/admin/dashboard', { replace: true });
        }, 100);
      } else {
        setError('Login failed - no user data received');
      }
    } catch (err) {
      setError(resetMode ? 'Could not send the reset email. Please try again later.' : 'Failed to log in');
      console.error('Login exception:', err);
    } finally {
      setLoading(false);
    }
  };

  const pageStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #2d2d2d 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem'
  };

  const containerStyle = {
    background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(17, 17, 17, 0.9) 100%)',
    borderRadius: '1rem',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(58, 58, 58, 0.5)',
    padding: '3rem',
    maxWidth: '400px',
    width: '100%',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
  };

  const logoStyle = {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '2rem'
  };

  const titleStyle = {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: 'white',
    textAlign: 'center',
    marginBottom: '0.5rem'
  };

  const subtitleStyle = {
    color: '#9ca3af',
    textAlign: 'center',
    marginBottom: '2rem',
    fontSize: '0.875rem'
  };

  const formGroupStyle = {
    marginBottom: '1.5rem'
  };

  const labelStyle = {
    display: 'block',
    color: 'white',
    fontWeight: '500',
    marginBottom: '0.5rem',
    fontSize: '0.875rem'
  };

  const inputStyle = {
    width: '100%',
    backgroundColor: 'rgba(58, 58, 58, 0.5)',
    border: '1px solid rgba(75, 85, 99, 0.5)',
    color: 'white',
    borderRadius: '0.5rem',
    padding: '0.75rem 1rem',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    outline: 'none'
  };

  const buttonStyle = {
    width: '100%',
    backgroundColor: '#dfa926',
    color: 'black',
    fontWeight: '600',
    padding: '0.875rem 1rem',
    borderRadius: '0.5rem',
    border: 'none',
    fontSize: '1rem',
    cursor: loading ? 'not-allowed' : 'pointer',
    transition: 'all 0.3s ease',
    opacity: loading ? 0.7 : 1
  };

  const errorStyle = {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    color: '#fca5a5',
    padding: '0.75rem 1rem',
    borderRadius: '0.5rem',
    marginBottom: '1.5rem',
    fontSize: '0.875rem'
  };

  const footerStyle = {
    textAlign: 'center',
    marginTop: '2rem',
    paddingTop: '1.5rem',
    borderTop: '1px solid rgba(58, 58, 58, 0.3)',
    color: '#6b7280',
    fontSize: '0.75rem'
  };

  return (
    <div style={pageStyle}>
      <div style={containerStyle}>
        <div style={logoStyle}>
          <img 
            src="/logo.png" 
            alt="Hestia Doors Logo"
            style={{ height: '4rem', width: 'auto' }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentNode.innerHTML = '<div style="font-size: 2rem; font-weight: bold; color: #dfa926;">HESTIA DOORS</div>';
            }}
          />
        </div>
        
        <h1 style={titleStyle}>Admin Portal</h1>
        <p style={subtitleStyle}>{resetMode ? 'Reset your password by email' : 'Sign in to access the dashboard'}</p>
        {resetMode && <p style={subtitleStyle}>If the email does not arrive, ask an account manager for a private reset link.</p>}
        {notice && <p role="status" style={{ color: '#a7f3d0' }}>{notice}</p>}
        
        {error && (
          <div style={errorStyle}>
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div style={formGroupStyle}>
            <label htmlFor="login-email" style={labelStyle}>Email Address</label>
            <input 
              type="email" 
              id="login-email" autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = '#dfa926'}
              onBlur={(e) => e.target.style.borderColor = 'rgba(75, 85, 99, 0.5)'}
              required 
              placeholder="Enter your email"
            />
          </div>
          
          {!resetMode && <div style={formGroupStyle}>
            <label htmlFor="login-password" style={labelStyle}>Password</label>
            <input 
              type="password" 
              id="login-password" autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = '#dfa926'}
              onBlur={(e) => e.target.style.borderColor = 'rgba(75, 85, 99, 0.5)'}
              required 
              placeholder="Enter your password"
            />
          </div>}
          
          <button 
            type="submit"
            disabled={loading}
            style={buttonStyle}
            onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = '#c8941f')}
            onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = '#dfa926')}
          >
            {loading ? 'Please wait…' : resetMode ? 'Send reset link' : 'Sign In'}
          </button>
        </form>
        <button type="button" disabled={loading} onClick={() => { setResetMode(v => !v); setError(''); setNotice(''); }} style={{ background: 'none', color: '#dfa926', border: 0, marginTop: 20 }}>{resetMode ? 'Back to sign in' : 'Forgot password?'}</button>
        
        <div style={footerStyle}>
          Access by invitation only<br/>
          Premium door manufacturing system
        </div>
      </div>
    </div>
  );
};

export default Login;
